import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import toast from 'react-hot-toast';
import {
  useBulkUploadDraft,
  useExtractBulkUploadFile,
  useSubmitBulkUpload,
  useUpdateBulkUploadDraft,
  useValidateBulkUpload,
} from '@/api';
import { Button, Card, CardContent } from '@/components/atoms';
import { PageHeader } from '@/components/molecules';
import { routes } from '@/lib/constants';
import { usePlanGate } from '@/components/organisms';
import { formatProductImageIds } from '@/lib/helpers/product-images';
import type {
  BulkUploadSpreadsheetRow,
  BulkUploadSubmitData,
  BulkUploadValidatedRow,
  BulkUploadValidationData,
} from '@/types';
import { ArrowLeft } from 'lucide-react';
import {
  BulkUploadDropzone,
  BulkUploadResultStep,
  BulkUploadStepIndicator,
  BulkUploadTemplateSection,
  BulkUploadValidationStep,
} from './components';

function draftRowToSpreadsheetRow(
  row: BulkUploadValidatedRow
): BulkUploadSpreadsheetRow {
  return {
    'Product Name': row.data.name,
    'Product Description': row.data.shortDescription,
    'More Details': row.data.description,
    Category: row.data.categoryName,
    'Selling Price':
      row.data.price != null && !Number.isNaN(row.data.price)
        ? String(row.data.price)
        : '',
    'Stock Quantity':
      row.data.quantity != null && !Number.isNaN(row.data.quantity)
        ? String(row.data.quantity)
        : '',
    'Sub-category': '',
    'Product Variations': '',
    Colour: '',
    Size: '',
    'Cost Price': '',
    'Discounted Price': '',
    SKU: '',
    Weight: '',
    'Product Images': formatProductImageIds(row.data.images ?? []),
  };
}

function getValidRowsForSubmit(
  spreadsheetRows: BulkUploadSpreadsheetRow[],
  validation: BulkUploadValidationData
): BulkUploadSpreadsheetRow[] {
  const validRowNumbers = new Set(
    validation.rows
      .filter((row) => row.status === 'valid')
      .map((row) => row.row)
  );

  return spreadsheetRows.filter((_, index) => validRowNumbers.has(index + 2));
}

export const BulkProductUploadPage = () => {
  const { allowsFeature } = usePlanGate();

  useEffect(() => {
    allowsFeature('bulkUpload');
  }, [allowsFeature]);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('sellyt-product-template.csv');
  const [rows, setRows] = useState<BulkUploadSpreadsheetRow[]>([]);
  const [validation, setValidation] = useState<BulkUploadValidationData | null>(
    null
  );
  const [uploadResult, setUploadResult] = useState<BulkUploadSubmitData | null>(
    null
  );
  const [draftLoaded, setDraftLoaded] = useState(false);

  const { data: draft, isFetched: isDraftFetched } = useBulkUploadDraft();
  const extractMutation = useExtractBulkUploadFile();
  const validateMutation = useValidateBulkUpload();
  const updateDraftMutation = useUpdateBulkUploadDraft();
  const submitMutation = useSubmitBulkUpload();

  const isBusy =
    extractMutation.isPending ||
    validateMutation.isPending ||
    updateDraftMutation.isPending ||
    submitMutation.isPending;

  useEffect(() => {
    if (draftLoaded || !isDraftFetched) return;

    if (!draft?.rows?.length) {
      setDraftLoaded(true);
      return;
    }

    setFileName(draft.fileName);
    setValidation(draft);
    setRows(draft.rows.map(draftRowToSpreadsheetRow));
    setStep(draft.errorCount > 0 || draft.validCount > 0 ? 2 : 1);
    setDraftLoaded(true);
  }, [draft, draftLoaded, isDraftFetched]);

  const canValidate = useMemo(
    () => !!selectedFile || rows.length > 0,
    [rows.length, selectedFile]
  );

  const resetFlow = () => {
    setStep(1);
    setSelectedFile(null);
    setFileName('sellyt-product-template.csv');
    setRows([]);
    setValidation(null);
    setUploadResult(null);
    setDraftLoaded(true);
  };

  const handleValidate = async () => {
    if (!allowsFeature('bulkUpload')) return;

    try {
      let nextRows = rows;
      let nextFileName = fileName;

      if (selectedFile) {
        const extracted = await extractMutation.mutateAsync(selectedFile);
        nextRows = extracted.data.rows;
        nextFileName = extracted.data.fileName;
        setRows(nextRows);
        setFileName(nextFileName);
      }

      if (nextRows.length === 0) {
        toast.error('The uploaded file is empty.');
        return;
      }

      const validated = await validateMutation.mutateAsync({
        fileName: nextFileName,
        rows: nextRows,
      });

      setValidation(validated.data);
      setStep(2);
      setDraftLoaded(true);
    } catch {
      // Errors are surfaced by the global mutation handler.
    }
  };

  const handleImport = async () => {
    if (!validation) return;

    const validRows = getValidRowsForSubmit(rows, validation);
    if (validRows.length === 0) {
      toast.error('No valid products to import.');
      return;
    }

    try {
      const response = await submitMutation.mutateAsync({ rows: validRows });
      setUploadResult(response.data);
      setStep(3);
    } catch {
      // Errors are surfaced by the global mutation handler.
    }
  };

  const handleRevalidate = async () => {
    if (rows.length === 0) return;

    try {
      await updateDraftMutation.mutateAsync({
        fileName,
        rows,
      });

      const validated = await validateMutation.mutateAsync({
        fileName,
        rows,
      });

      setValidation(validated.data);
      setDraftLoaded(true);
    } catch {
      // Errors are surfaced by the global mutation handler.
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to={routes.products}
        className="inline-flex w-fit items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to Products
      </Link>

      <PageHeader
        heading="Bulk Product Upload"
        description="Upload multiple products at once using our downloadable template. Fill in your product information, upload the completed file, and we'll validate everything before importing your products."
      />

      <BulkUploadStepIndicator currentStep={step} />

      {step === 1 && (
        <div className="space-y-6">
          <BulkUploadTemplateSection />

          <Card className="border shadow-none">
            <CardContent className="space-y-4 pt-6">
              <BulkUploadDropzone
                file={selectedFile}
                disabled={isBusy}
                onFileChange={setSelectedFile}
              />
              <div className="flex justify-end">
                <Button
                  type="button"
                  isLoading={isBusy}
                  disabled={!canValidate}
                  onClick={() => void handleValidate()}
                >
                  Validate File
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {step === 2 && validation && (
        <BulkUploadValidationStep
          validation={validation}
          rows={rows}
          isSubmitting={submitMutation.isPending}
          isRevalidating={validateMutation.isPending}
          onRowsChange={setRows}
          onBack={() => setStep(1)}
          onRevalidate={() => void handleRevalidate()}
          onImport={() => void handleImport()}
        />
      )}

      {step === 3 && uploadResult && (
        <BulkUploadResultStep result={uploadResult} onStartOver={resetFlow} />
      )}
    </div>
  );
};
